import * as ko from "knockout";
import * as localisationHelper from 'localisationHelper';

import { IResourceText } from "Content/ts/Shared/Localisation/Models/resourceText";

export interface IStandardButtonsBase {
    ClickEvent: Function;                                                           // Required
    ButtonText: string | KnockoutObservable<string>;                                // Required
    LocButtonText: IResourceText;

    FullLengthButton: boolean;                                                      // Optional
    ShowLoading: KnockoutObservable<boolean>;                                       // Optional
    DisplayCondition: KnockoutObservable<boolean>;                                  // Optional
    DisableCondition: KnockoutObservable<boolean>;                                  // Optional
    CssClasses: string;                                                             // Optional
    Id: string;                                                                     // Optional
    TestId?: string | string[];                                                     // Optional
    AriaControls: string | KnockoutObservable<string>                               // Optional
    AriaExpanded: boolean| KnockoutObservable<boolean>                              // Optional
}

export default class StandardButtonsBase {
    fullLengthButton: boolean;

    clickEvent: Function;

    displayCondition: KnockoutObservable<boolean>;
    disableCondition: KnockoutObservable<boolean>;
    showLoading: KnockoutObservable<boolean>;

    buttonText: string | KnockoutObservable<string>;
    id: string;
    testId: string | string[];
    ariaControls: string | KnockoutObservable<string>;
    ariaExpanded: boolean | KnockoutObservable<boolean>;
    ariaExpandedComputed: KnockoutComputed<string>;

    _cssClasses: string;
    cssClasses: KnockoutComputed<string>;

    constructor(params: IStandardButtonsBase) {
        // Provided Component Properties
        this.id = params.Id;
        this.testId = params.TestId ?? '';

        if (params.ButtonText && params.LocButtonText) {
            throw new Error("Please only provide text or loc text for the button");
        }

        if (!params.ButtonText && !params.LocButtonText) {
            throw new Error("Please provide text or loc text for the button");
        }

        this.buttonText = (params.ButtonText)
            ? params.ButtonText
            : localisationHelper.getString(params.LocButtonText.Section, params.LocButtonText.Key);

        this.clickEvent = params.ClickEvent;
        this.fullLengthButton = params.FullLengthButton || false;
        this.displayCondition = params.DisplayCondition || ko.observable(true);
        this.disableCondition = params.DisableCondition || ko.observable(false);
        this.showLoading = params.ShowLoading || ko.observable(false);
        this._cssClasses = params.CssClasses || "";
        this.ariaControls = params.AriaControls ?? undefined;
        this.ariaExpanded = params.AriaExpanded ?? undefined;
        
        if (this.fullLengthButton) {
            this._cssClasses += " btn-block";
        }

        this.setupComputeds();
    }

    private setupComputeds() {
        this.cssClasses = ko.pureComputed(() => this._cssClasses);

        this.ariaExpandedComputed = ko.pureComputed(() => {
            //In order to meet accessibility, when the value of 'aria-computed' is false, 
            //  we must still have the attribute on the element and have it explicitly set to "false".
            //  However, when knockout encounters a false boolean in the attr binding, it removes the attribute
            //  Therefore to work around this, we must toString it and bind that.
            var value = ko.unwrap(this.ariaExpanded);

            if (value === null || value === undefined) {
                return undefined;
            }
            return value.toString();
        });
    }

    onClick() {
        if (this.disableCondition()) {
            return;
        }

        this.clickEvent();
    }
}

const buttonDarkOutline = "button-dark-outline";
ko.components.unregister(buttonDarkOutline);
ko.components.register(buttonDarkOutline, {
    viewModel: StandardButtonsBase,
    template: { require: `text!Content/ts/Shared/Buttons/StandardButtons/Dark/${buttonDarkOutline}.html` }
});

const buttonSuccessComponent = "button-success";
ko.components.unregister(buttonSuccessComponent);
ko.components.register(buttonSuccessComponent, {
    viewModel: StandardButtonsBase,
    template: { require: `text!Content/ts/Shared/Buttons/StandardButtons/Success/${buttonSuccessComponent}.html` }
});

const buttonDangerComponent = "button-danger";
ko.components.unregister(buttonDangerComponent);
ko.components.register(buttonDangerComponent, {
    viewModel: StandardButtonsBase,
    template: { require: `text!Content/ts/Shared/Buttons/StandardButtons/Danger/${buttonDangerComponent}.html` }
});

const buttonDangerInverse = "button-danger-inverse";
ko.components.unregister(buttonDangerInverse);
ko.components.register(buttonDangerInverse, {
    viewModel: StandardButtonsBase,
    template: { require: `text!Content/ts/Shared/Buttons/StandardButtons/Danger/${buttonDangerInverse}.html` }
});